import React, { useMemo, useCallback } from 'react'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import useUserMultiCartBedbankHotelItemData from '../../hooks/useUserMultiCartBedbankHotelItemData'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { MultiCartReviewMachineContext } from '../../MultiCartReviewMachineContext'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'

interface Props {
  multiCartItems: Array<App.MultiCartFlightItem | App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem>
  tileVariant?: 'default' | 'condensed'
  className?: string
  hideTitleAndDates?: boolean
  hideButtons?: boolean
}

function BedbankHotelTile({
  multiCartItems,
  tileVariant = 'default',
  className,
  hideTitleAndDates = false,
  hideButtons = false,
}: Props) {
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  // Separate flight and hotel items
  const hotelItem = useMemo(() => {
    return multiCartItems.find(item => !isFlightItem(item.item)) as App.MultiCartBedbankHotelItem | undefined
  }, [multiCartItems])

  const flightItems = useMemo(() => {
    return multiCartItems.filter(item => isFlightItem(item.item)) as Array<App.MultiCartFlightItem>
  }, [multiCartItems])

  const data = useUserMultiCartBedbankHotelItemData(hotelItem!, false)

  const formattedDateRange = useMemo(() => {
    if (!hotelItem?.checkIn || !hotelItem?.checkOut) return false
    return `${formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)} · ${hotelItem.duration} nights`
  }, [hotelItem?.checkIn, hotelItem?.checkOut, hotelItem?.duration])

  const formattedOccupancy = useMemo(() => {
    if (!hotelItem?.occupancy) return false
    return formatOccupantsShort(hotelItem.occupancy)
  }, [hotelItem?.occupancy])

  const { publicTotal: hotelPublicTotal, memberTotal: hotelMemberTotal } = useMemo(
    () => getUserCartItemDisplayTotals(data.itemTotals),
    [data?.itemTotals]
  )

  // Calculate flight prices
  const flightTotal = useMemo(() => {
    return flightItems.reduce((total, flightItem) => {
      const flightCheckoutItem = flightItem.item as App.Checkout.FlightItem
      return total + getFlightsTotal(flightCheckoutItem)
    }, 0)
  }, [flightItems])

  const numberOfRoomsAssociatedWithFlight = flightItems[0]?.bundledItemIds?.length ?? 1
  const flightTotalPerRoom = flightTotal / numberOfRoomsAssociatedWithFlight
  // Calculate bundled prices (hotel + flight)
  const bundledPublicTotal = hotelPublicTotal + flightTotalPerRoom
  const bundledMemberTotal = hotelMemberTotal === 0 ? 0 : hotelMemberTotal + flightTotalPerRoom

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)
  const image = data.package?.images[0] || data.offer?.image

  // Check if all items are saved for later
  const areAllItemsSavedForLater = useMemo(
    () => multiCartItems.every(item => item.state === 'save_for_later'),
    [multiCartItems]
  )

  const isBookable = useMemo(
    () => !multiCartItems.some(item => item.state === 'deleted') && data.isBookable,
    [multiCartItems, data.isBookable]
  )

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => multiCartItems.map(item => item.id), [multiCartItems])

  // Get current states for all items
  const currentStates = useMemo(
    () => multiCartItems.reduce((acc, item) => {
      acc[item.id] = item.state
      return acc
    }, {} as Record<string, App.MultiCartItemState>),
    [multiCartItems]
  )

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: allItemIds,
      saveForLater: !areAllItemsSavedForLater,
      skipNotification: true,
    })
  }, [activeCartId, allItemIds, areAllItemsSavedForLater, multiCartReviewActorRef])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: allItemIds,
      currentStates,
    })
  }, [activeCartId, allItemIds, currentStates, multiCartActorRef])

  if (data.loading || !hotelItem) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        content={<CondensedCartReviewTileContent
          title={hideTitleAndDates ? undefined : data.offer?.property.name || data.offer?.name || ''}
          dates={hideTitleAndDates ? undefined : formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        onDelete={hideButtons ? undefined : handleDelete}
      />
    )
  }

  const actions = hideButtons ? undefined : (
    <CartReviewTileActions
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />
  )

  return (
    <CartReviewTile
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={{
          calculationRequests: [],
          calculationType: 'exact',
        }}
      />}
      price={<CartReviewPriceStack
        price={bundledPublicTotal}
        memberPrice={bundledMemberTotal}
        value={data.itemTotals?.value ?? 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />}
      banner={<CartReviewBanner variant={bannerTypeVariant} />}
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      actions={actions}
      className={className}
      hideAvailabilityDetails={false}
      disableOverlay={false}
    />
  )
}

export default BedbankHotelTile
